import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useRascunho, type ConfigRascunho } from '../useRascunho';
import {
  combinarDataHora,
  draftDeEvento,
  eventoDraftVazio,
  separarDataHora,
  slugificar,
} from '@/pages/assistente-evento/utils';
import type { EventoDraft, ImagemDraft } from '@/pages/assistente-evento/types';
import type { EstadoSalvamento } from '@/pages/assistente-evento/types';
import { nomeDeLote, novoIngresso, type Ingresso } from './types';

const CHAVE_RASCUNHO = 'rascunho:ingresso';
const CATEGORIA_PADRAO = 'show';

async function garantirSlug(
  nome: string,
  slugAtual: string,
  eventoIdAtual: number | null,
): Promise<string> {
  const existente = slugAtual.trim();
  if (existente) return existente;
  const base = slugificar(nome) || 'evento';
  let candidato = base;
  let sufixo = 2;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let q = supabase.from('eventos').select('id').eq('slug', candidato);
    if (eventoIdAtual) q = q.neq('id', eventoIdAtual);
    const { data } = await q.maybeSingle();
    if (!data) return candidato;
    candidato = `${base}-${sufixo}`;
    sufixo += 1;
  }
}

async function processarArte(
  imagem: ImagemDraft,
  eventoIdNum: number,
): Promise<string | null | undefined> {
  if (imagem.arquivo) {
    const nome = imagem.arquivo.name || 'arte.jpg';
    const ext = nome.split('.').pop()?.toLowerCase() || 'jpg';
    const caminho = `${eventoIdNum}/arte-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('capas').upload(caminho, imagem.arquivo);
    if (error) throw error;
    const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
    return data.publicUrl;
  }
  if (imagem.removida) return null;
  return undefined;
}

function ingressoDeLinha(m: Record<string, unknown>): Ingresso {
  const abertura = separarDataHora((m.inscricoes_abrem_em as string) || '');
  const fechamento = separarDataHora((m.inscricoes_fecham_em as string) || '');
  const vagas = m.vagas_total as number | null;
  const valorCentavos = (m.valor_centavos as number) || 0;
  const valorDefinido = m.valor_definido === undefined ? true : (m.valor_definido as boolean);
  return {
    id: m.id as number,
    nome: (m.nome as string) || '',
    valorModo: !valorDefinido ? 'a_definir' : valorCentavos > 0 ? 'pago' : 'gratuito',
    valorCentavos,
    temLimite: vagas !== null && vagas !== undefined,
    vagas,
    aVendaDeData: abertura.data,
    aVendaDeHora: abertura.hora,
    aVendaAteData: fechamento.data,
    aVendaAteHora: fechamento.hora,
    maxPorCompra: (m.max_por_compra as number) || 10,
    descricao: (m.descricao as string) || '',
  };
}

export function useIngresso() {
  const slugRef = useRef('');
  const mudarDraftRef = useRef<(patch: Partial<EventoDraft>) => void>(() => {});
  const [perfilCidade, setPerfilCidade] = useState('');
  const [perfilUf, setPerfilUf] = useState('');
  const [ingressos, setIngressos] = useState<Ingresso[]>([]);
  const ingressosRef = useRef<Ingresso[]>([]);

  // Salvamento dos lotes (mesmo autosave do Passo 1).
  const [estadoIngressos, setEstadoIngressos] = useState<EstadoSalvamento>('ocioso');
  const sujoIngressosRef = useRef(false);
  const versaoIngressosRef = useRef(0);
  const salvandoIngressosRef = useRef(false);
  const eventoIdAtualRef = useRef<number | null>(null);
  const debounceIngRef = useRef<number | null>(null);
  const timerIngRef = useRef<number | null>(null);

  useEffect(() => {
    ingressosRef.current = ingressos;
  }, [ingressos]);

  function definirIngressos(lista: Ingresso[]) {
    versaoIngressosRef.current += 1;
    sujoIngressosRef.current = true;
    ingressosRef.current = lista;
    setIngressos(lista);
  }

  async function salvarIngressos(eventoId: number, lista: Ingresso[]) {
    const idsAtuais: number[] = [];
    const novaLista = lista.map((p) => ({ ...p }));

    for (let i = 0; i < novaLista.length; i += 1) {
      const p = novaLista[i];
      const dados = {
        evento_id: eventoId,
        nome: p.nome.trim(),
        descricao: p.descricao || null,
        publica: true,
        ativa: true,
        valor_centavos: p.valorModo === 'pago' ? p.valorCentavos : 0,
        valor_definido: p.valorModo !== 'a_definir',
        vagas_total: p.temLimite ? p.vagas : null,
        inscricoes_abrem_em: combinarDataHora(p.aVendaDeData, p.aVendaDeHora),
        inscricoes_fecham_em: combinarDataHora(p.aVendaAteData, p.aVendaAteHora),
        max_por_compra: p.maxPorCompra,
        ordem: i,
      };

      if (p.id) {
        const { error } = await supabase.from('modalidades').update(dados).eq('id', p.id);
        if (error) throw error;
        idsAtuais.push(p.id);
      } else {
        const { data, error } = await supabase
          .from('modalidades')
          .insert(dados)
          .select('id')
          .single();
        if (error) throw error;
        const novoId = (data as { id: number }).id;
        novaLista[i] = { ...p, id: novoId };
        idsAtuais.push(novoId);
      }
    }

    // Ingressos que saíram da lista (rascunho, sem compras ainda) são apagados.
    const { data: existentes } = await supabase
      .from('modalidades')
      .select('id')
      .eq('evento_id', eventoId);
    const removidos = ((existentes as { id: number }[]) || [])
      .map((e) => e.id)
      .filter((id) => !idsAtuais.includes(id));
    if (removidos.length > 0) {
      await supabase.from('modalidades').delete().in('id', removidos);
    }

    setIngressos(novaLista);
    ingressosRef.current = novaLista;
  }

  // Grava os lotes agora (chamado pelo autosave, pelo Voltar e pelo Sair).
  async function salvarIngressosAgora(): Promise<boolean> {
    const id = eventoIdAtualRef.current;
    if (!id) return true;
    if (salvandoIngressosRef.current) return false;

    salvandoIngressosRef.current = true;
    const versaoNoInicio = versaoIngressosRef.current;
    setEstadoIngressos('salvando');
    try {
      await salvarIngressos(id, ingressosRef.current);
      if (versaoIngressosRef.current === versaoNoInicio) sujoIngressosRef.current = false;
      setEstadoIngressos('salvo');
      if (timerIngRef.current) window.clearTimeout(timerIngRef.current);
      timerIngRef.current = window.setTimeout(() => setEstadoIngressos('ocioso'), 2500);
      return true;
    } catch {
      sujoIngressosRef.current = true;
      setEstadoIngressos(navigator.onLine === false ? 'offline' : 'ocioso');
      return false;
    } finally {
      salvandoIngressosRef.current = false;
    }
  }

  const config: ConfigRascunho<EventoDraft> = {
    chaveLocal: CHAVE_RASCUNHO,
    tabela: 'eventos',
    rotaVoltar: '/criar/ingresso',
    draftInicial: {
      ...eventoDraftVazio(),
      categoria: CATEGORIA_PADRAO,
      formaParticipar: 'ingresso',
    },
    retomar: async (id, userId) => {
      const { data: evRes } = await supabase
        .from('eventos')
        .select('*')
        .eq('id', id)
        .eq('dono_id', userId)
        .eq('status', 'rascunho')
        .maybeSingle();
      if (!evRes) return null;
      slugRef.current = (evRes.slug as string) || '';
      return {
        atualizado_em: (evRes.atualizado_em as string) || null,
        draft: draftDeEvento(evRes as Record<string, unknown>),
      };
    },
    paraColunas: async (d, id) => {
      const slug = await garantirSlug(d.nome, d.slug, id);
      slugRef.current = slug;
      const inicio = combinarDataHora(d.inicioData, d.inicioHora);
      const fimCalc = combinarDataHora(d.fimData, d.fimHora);
      const fim =
        fimCalc ||
        (inicio ? new Date(new Date(inicio).getTime() + 3 * 3600 * 1000).toISOString() : null);
      return {
        nome: d.nome.trim(),
        slug,
        descricao: d.descricao || null,
        categoria: d.categoria || CATEGORIA_PADRAO,
        local_nome: d.localNome || null,
        endereco: d.endereco || null,
        cidade: d.cidade.trim(),
        uf: d.uf,
        cep: d.cep || null,
        inicio_em: inicio,
        fim_em: fim,
      };
    },
    criar: async (colunas) => {
      const { data: novoId, error } = await supabase.rpc('criar_evento', {
        p_nome: colunas.nome,
        p_slug: colunas.slug,
        p_descricao: colunas.descricao || '',
        p_categoria: colunas.categoria,
        p_local_nome: colunas.local_nome || '',
        p_endereco: colunas.endereco || '',
        p_cidade: colunas.cidade,
        p_uf: colunas.uf,
        p_cep: colunas.cep || '',
        p_inicio_em: colunas.inicio_em,
        p_fim_em: colunas.fim_em,
      });
      if (error) throw error;
      const id = Number(novoId);
      await supabase.from('eventos').update({ vende_ingressos: true }).eq('id', id);
      await supabase.rpc('definir_usa_equipes', { p_evento: id, p_valor: false });
      return id;
    },
    aposSalvar: async (id, d) => {
      const url = await processarArte(d.imagemDesktop, id);
      if (url === undefined) return;
      await supabase
        .from('eventos')
        .update({ imagem_desktop_url: url, imagem_mobile_url: url })
        .eq('id', id);
      mudarDraftRef.current({
        imagemDesktop: { arquivo: null, url, removida: false, carregando: false },
        imagemMobile: { arquivo: null, url, removida: false, carregando: false },
      });
    },
    podeSalvar: (d) => !!(d.nome.trim() && d.inicioData && d.inicioHora),
    publicarRegistro: async (id) => {
      await salvarIngressos(id, ingressosRef.current);
      // Ingresso sem data de "à venda de" abre na hora da publicação.
      await supabase
        .from('modalidades')
        .update({ inscricoes_abrem_em: new Date().toISOString() })
        .eq('evento_id', id)
        .is('inscricoes_abrem_em', null);
      await supabase
        .from('eventos')
        .update({
          categoria: CATEGORIA_PADRAO,
          vende_ingressos: true,
          status: 'publicado',
          publicado_em: new Date().toISOString(),
        })
        .eq('id', id);
    },
    destinoPublicado: () => `/e/${slugRef.current}`,
  };

  const motor = useRascunho(config);
  const { draft, mudarDraft } = motor;
  mudarDraftRef.current = mudarDraft;

  useEffect(() => {
    eventoIdAtualRef.current = motor.registroId;
  }, [motor.registroId]);

  // Autosave dos lotes: 1,2 s depois de parar de mexer.
  useEffect(() => {
    if (!eventoIdAtualRef.current) return;
    if (!sujoIngressosRef.current) return;
    if (debounceIngRef.current) window.clearTimeout(debounceIngRef.current);
    debounceIngRef.current = window.setTimeout(() => {
      void salvarIngressosAgora();
    }, 1200);
    return () => {
      if (debounceIngRef.current) window.clearTimeout(debounceIngRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingressos]);

  // Sair da aba ou perder a conexão com algo pendente: tenta gravar.
  useEffect(() => {
    function aoEsconder() {
      if (document.visibilityState === 'hidden' && sujoIngressosRef.current) {
        void salvarIngressosAgora();
      }
    }
    function aoVoltarOnline() {
      if (sujoIngressosRef.current) void salvarIngressosAgora();
    }
    document.addEventListener('visibilitychange', aoEsconder);
    window.addEventListener('online', aoVoltarOnline);
    return () => {
      document.removeEventListener('visibilitychange', aoEsconder);
      window.removeEventListener('online', aoVoltarOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cidade/UF pré-preenchidos do perfil (só quando não retomou rascunho).
  useEffect(() => {
    if (motor.estado !== 'ok') return;
    let ativo = true;

    async function carregarPerfil() {
      const { data: perfilData } = await supabase
        .from('vw_meu_perfil')
        .select('cidade, uf')
        .maybeSingle();
      const pCidade = ((perfilData as { cidade?: string | null } | null)?.cidade) || '';
      const pUf = ((perfilData as { uf?: string | null } | null)?.uf) || '';
      if (ativo) {
        setPerfilCidade(pCidade);
        setPerfilUf(pUf);
        mudarDraft({
          categoria: CATEGORIA_PADRAO,
          formaParticipar: 'ingresso',
          cidade: draft.cidade || pCidade,
          uf: draft.uf || pUf,
        });
      }
    }

    carregarPerfil();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor.estado]);

  // Carrega os ingressos já salvos, ou semeia o primeiro "Inteira".
  useEffect(() => {
    if (motor.estado !== 'ok') return;
    let ativo = true;

    async function carregarIngressos() {
      const id = motor.registroId;
      if (!id) {
        if (ativo) setIngressos([{ ...novoIngresso(), nome: nomeDeLote(1) }]);
        return;
      }
      const { data } = await supabase
        .from('modalidades')
        .select(
          'id, nome, descricao, valor_centavos, valor_definido, vagas_total, inscricoes_abrem_em, inscricoes_fecham_em, max_por_compra',
        )
        .eq('evento_id', id)
        .order('ordem');
      const mods = (data as Record<string, unknown>[]) || [];
      if (!ativo) return;
      if (mods.length === 0) {
        setIngressos([{ ...novoIngresso(), nome: nomeDeLote(1) }]);
      } else {
        setIngressos(mods.map(ingressoDeLinha));
      }
    }

    carregarIngressos();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor.estado, motor.registroId]);

  return {
    estado: motor.estado,
    erro: motor.erro,
    setErro: motor.setErro,
    eventoId: motor.registroId,
    draft,
    mudarDraft,
    perfilCidade,
    perfilUf,
    estadoSalvamento: motor.estadoSalvamento,
    salvando: motor.salvando,
    ingressos,
    setIngressos: definirIngressos,
    estadoIngressos,
    salvarIngressosAgora,
    salvarEvento: motor.salvar,
    publicar: motor.publicar,
  };
}